/**
 * AI-Assisted Segue Component
 * 
 * Allows users to either write their good news or use AI voice recording
 * to transcribe and clean up their spoken responses.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Mic, MicOff, Pencil, Sparkles, StopCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sound wave animation component
const SoundWave = ({ isActive }) => {
  return (
    <div className="flex items-center justify-center gap-1 h-16">
      {[...Array(12)].map((_, i) => (
        <div
          key={i}
          className={`w-1 bg-cyan-500 rounded-full transition-all duration-150 ${
            isActive ? 'animate-pulse' : ''
          }`}
          style={{
            height: isActive ? `${Math.random() * 40 + 20}%` : '20%',
            animationDelay: `${i * 0.1}s`,
            animationDuration: '0.5s'
          }}
        />
      ))}
    </div>
  );
};

// Audio visualizer for recording
const AudioVisualizer = ({ analyser, isRecording }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    if (!analyser || !canvasRef.current || !isRecording) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      animationRef.current = requestAnimationFrame(draw);
      analyser.getByteFrequencyData(dataArray);

      ctx.fillStyle = 'rgba(15, 23, 42, 0.2)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const barWidth = (canvas.width / bufferLength) * 2.5;
      let x = 0;

      for (let i = 0; i < bufferLength; i++) {
        const barHeight = (dataArray[i] / 255) * canvas.height * 0.8;
        
        // Cyan gradient
        const gradient = ctx.createLinearGradient(0, canvas.height - barHeight, 0, canvas.height);
        gradient.addColorStop(0, '#06b6d4');
        gradient.addColorStop(1, '#0891b2');
        
        ctx.fillStyle = gradient;
        ctx.fillRect(x, canvas.height - barHeight, barWidth, barHeight);
        x += barWidth + 1;
      }
    };

    draw();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [analyser, isRecording]);

  if (!isRecording) {
    return (
      <div className="h-20 flex items-center justify-center text-slate-500 text-sm">
        Press the microphone to start recording
      </div>
    );
  }

  return (
    <canvas 
      ref={canvasRef} 
      width={300} 
      height={80} 
      className="w-full h-20 rounded-lg"
    />
  );
};

const AIAssistedSegue = ({ currentMember, onComplete }) => {
  const [mode, setMode] = useState('write'); // 'write' or 'ai'
  const [personalWin, setPersonalWin] = useState('');
  const [professionalWin, setProfessionalWin] = useState('');
  
  // AI recording state
  const [recordingField, setRecordingField] = useState(null); // 'personal' or 'professional'
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [analyser, setAnalyser] = useState(null);
  const [micPermission, setMicPermission] = useState('unknown'); // 'unknown', 'granted', 'denied', 'unavailable'
  
  // Refs for recording
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioContextRef = useRef(null);
  const streamRef = useRef(null);

  const memberName = currentMember?.display_name?.split(' ')[0] || 'Team member';

  // Check microphone availability on mount
  React.useEffect(() => {
    const checkMicPermission = async () => {
      // Check if browser supports getUserMedia
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setMicPermission('unavailable');
        return;
      }
      
      try {
        // Check permission status if available
        if (navigator.permissions && navigator.permissions.query) {
          const result = await navigator.permissions.query({ name: 'microphone' });
          setMicPermission(result.state); // 'granted', 'denied', or 'prompt'
          
          result.addEventListener('change', () => {
            setMicPermission(result.state);
          });
        }
      } catch (error) {
        // Permission query not supported, we'll find out when user tries to record
        setMicPermission('unknown');
      }
    };
    
    checkMicPermission();
  }, []);

  const startRecording = async (field) => {
    // Check browser support first
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast.error('Your browser does not support audio recording. Please use Chrome, Firefox, or Edge.');
      return;
    }
    
    try {
      // Request microphone permission
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        } 
      });
      streamRef.current = stream;
      setMicPermission('granted');
      
      // Set up audio context for visualization
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const analyserNode = audioContext.createAnalyser();
      analyserNode.fftSize = 256;
      source.connect(analyserNode);
      setAnalyser(analyserNode);
      
      // Determine best supported mime type
      let mimeType = 'audio/webm';
      if (!MediaRecorder.isTypeSupported('audio/webm')) {
        if (MediaRecorder.isTypeSupported('audio/mp4')) {
          mimeType = 'audio/mp4';
        } else if (MediaRecorder.isTypeSupported('audio/ogg')) {
          mimeType = 'audio/ogg';
        }
      }
      
      // Set up media recorder
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await processAudio(audioBlob, field);
      };
      
      mediaRecorder.start();
      setRecordingField(field);
      setIsRecording(true);
      toast.info(`Recording ${field === 'personal' ? 'personal' : 'professional'} win...`);
      
    } catch (error) {
      console.error('Error accessing microphone:', error);
      setMicPermission('denied');
      
      // Provide specific error messages
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        toast.error('Microphone access denied. Please allow microphone access in your browser settings and reload the page.');
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        toast.error('No microphone found. Please connect a microphone and try again.');
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        toast.error('Microphone is in use by another application. Please close other apps using the microphone.');
      } else if (error.name === 'OverconstrainedError') {
        toast.error('Could not find a suitable microphone. Please try with default settings.');
      } else {
        toast.error(`Microphone error: ${error.message || 'Unknown error'}. Try refreshing the page.`);
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsProcessing(true);
      
      // Stop all tracks
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      
      // Close audio context
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    }
  };

  const processAudio = async (audioBlob, field) => {
    try {
      const formData = new FormData();
      formData.append('audio_file', audioBlob, 'recording.webm');
      formData.append('context', field === 'personal' ? 'personal good news or win' : 'professional work achievement or win');
      
      const response = await axios.post(`${API}/eos/ai/transcribe-and-cleanup`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const { cleaned_text } = response.data;
      
      if (field === 'personal') {
        setPersonalWin(cleaned_text);
      } else {
        setProfessionalWin(cleaned_text);
      }
      
      toast.success('Response captured and cleaned up!');
      
    } catch (error) {
      console.error('Error processing audio:', error);
      toast.error('Failed to process recording. Please try again.');
    } finally {
      setIsProcessing(false);
      setRecordingField(null);
      setAnalyser(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Good News - Personal & Professional</h3>
        <Tabs value={mode} onValueChange={setMode} className="w-auto">
          <TabsList className="h-8">
            <TabsTrigger value="write" className="text-xs px-3 h-7">
              <Pencil className="h-3 w-3 mr-1" />
              Write
            </TabsTrigger>
            <TabsTrigger value="ai" className="text-xs px-3 h-7">
              <Sparkles className="h-3 w-3 mr-1" />
              AI Assisted
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      
      <p className="text-sm text-slate-400">
        {currentMember 
          ? `${memberName}, share one personal and one professional win from the past week.`
          : 'Each team member shares one personal and one professional win.'}
      </p>
      
      {mode === 'ai' && micPermission === 'unavailable' && (
        <div className="p-3 bg-red-500/10 rounded-lg border border-red-500/30 text-xs text-red-400">
          <MicOff className="h-4 w-4 inline mr-2" />
          Your browser doesn't support audio recording. Please use Chrome, Firefox, or Edge.
        </div>
      )}
      
      {mode === 'ai' && micPermission === 'denied' && (
        <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/30 text-xs text-amber-400">
          <MicOff className="h-4 w-4 inline mr-2" />
          Microphone access denied. Click the lock icon in your browser's address bar to enable microphone access, then refresh the page.
        </div>
      )}
      
      {mode === 'ai' && micPermission !== 'unavailable' && micPermission !== 'denied' && (
        <div className="p-3 bg-cyan-500/10 rounded-lg border border-cyan-500/30 text-xs text-cyan-400">
          <Sparkles className="h-4 w-4 inline mr-2" />
          AI Mode: Click the microphone to record. AI will transcribe and clean up your response.
        </div>
      )}
      
      <div className="grid grid-cols-2 gap-4">
        {/* Personal Win */}
        <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/30">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-blue-400">🏠 Personal Win</h4>
            {mode === 'ai' && (
              <Button
                variant="ghost"
                size="sm"
                className={`h-8 w-8 p-0 ${isRecording && recordingField === 'personal' ? 'bg-red-500/20 text-red-400' : 'text-cyan-400 hover:bg-cyan-500/20'}`}
                onClick={() => isRecording && recordingField === 'personal' ? stopRecording() : startRecording('personal')}
                disabled={isProcessing || (isRecording && recordingField !== 'personal')}
              >
                {isProcessing && recordingField === 'personal' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : isRecording && recordingField === 'personal' ? (
                  <StopCircle className="h-4 w-4" />
                ) : (
                  <Mic className="h-4 w-4" />
                )}
              </Button>
            )}
          </div>
          
          {mode === 'ai' && isRecording && recordingField === 'personal' ? (
            <div className="bg-slate-900/50 rounded-lg p-2">
              <AudioVisualizer analyser={analyser} isRecording={isRecording} />
              <p className="text-xs text-center text-cyan-400 mt-2 animate-pulse">Recording... Click stop when done</p>
            </div>
          ) : mode === 'ai' && isProcessing && recordingField === 'personal' ? (
            <div className="h-[76px] flex items-center justify-center bg-slate-900/50 rounded-lg">
              <div className="text-center">
                <Loader2 className="h-6 w-6 animate-spin text-cyan-400 mx-auto" />
                <p className="text-xs text-slate-400 mt-2">AI processing...</p>
              </div>
            </div>
          ) : (
            <Textarea 
              placeholder="Share a personal highlight..." 
              rows={3} 
              className="bg-transparent border-blue-500/30"
              value={personalWin}
              onChange={(e) => setPersonalWin(e.target.value)}
              disabled={mode === 'ai' && isRecording}
            />
          )}
        </div>
        
        {/* Professional Win */}
        <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/30">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-green-400">💼 Professional Win</h4>
            {mode === 'ai' && (
              <Button
                variant="ghost"
                size="sm"
                className={`h-8 w-8 p-0 ${isRecording && recordingField === 'professional' ? 'bg-red-500/20 text-red-400' : 'text-cyan-400 hover:bg-cyan-500/20'}`}
                onClick={() => isRecording && recordingField === 'professional' ? stopRecording() : startRecording('professional')}
                disabled={isProcessing || (isRecording && recordingField !== 'professional')}
              >
                {isProcessing && recordingField === 'professional' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : isRecording && recordingField === 'professional' ? (
                  <StopCircle className="h-4 w-4" />
                ) : (
                  <Mic className="h-4 w-4" />
                )}
              </Button>
            )}
          </div>
          
          {mode === 'ai' && isRecording && recordingField === 'professional' ? (
            <div className="bg-slate-900/50 rounded-lg p-2">
              <AudioVisualizer analyser={analyser} isRecording={isRecording} />
              <p className="text-xs text-center text-cyan-400 mt-2 animate-pulse">Recording... Click stop when done</p>
            </div>
          ) : mode === 'ai' && isProcessing && recordingField === 'professional' ? (
            <div className="h-[76px] flex items-center justify-center bg-slate-900/50 rounded-lg">
              <div className="text-center">
                <Loader2 className="h-6 w-6 animate-spin text-cyan-400 mx-auto" />
                <p className="text-xs text-slate-400 mt-2">AI processing...</p>
              </div>
            </div>
          ) : (
            <Textarea 
              placeholder="Share a work highlight..." 
              rows={3} 
              className="bg-transparent border-green-500/30"
              value={professionalWin}
              onChange={(e) => setProfessionalWin(e.target.value)}
              disabled={mode === 'ai' && isRecording}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AIAssistedSegue;
